import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusChip from "../components/StatusChip";
import PhotoInput from "../components/PhotoInput";
import { ArrowLeft } from "lucide-react";

const d = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" }) : "—");
const inputCls = "mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2240]";

function HandoverModal({ v, onClose, onDone }) {
  const [odo, setOdo] = useState(v.current_odometer || "");
  const [odoPhoto, setOdoPhoto] = useState(null);
  const [fuel, setFuel] = useState("1/2");
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState({});
  const slots = [["front", "front view"], ["rear", "rear view"], ["left", "left side"], ["right", "right side"], ["interior", "interior"], ["odo", "odometer close-up"]];
  const submit = async () => {
    const pics = Object.values(photos).filter(Boolean);
    if (!odo || !odoPhoto) { toast.error("Final odometer reading and odometer photo are mandatory"); return; }
    if (pics.length < 4) { toast.error("At least 4 condition photos are required (front, rear, both sides, interior)"); return; }
    try {
      await api.post(`/vehicles/${v.id}/handover`, { final_odometer: parseInt(odo), odometer_photo: odoPhoto, fuel_level: fuel, condition_notes: notes, photos: pics });
      toast.success("Handover raised — waiting for VP approval");
      onClose(); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" onClick={onClose}>
      <div data-testid="handover-modal" className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-heading text-lg font-bold text-[#0E2240]">Hand over to Accounts · {v.reg_no}</h2>
        <p className="mt-1 text-xs text-slate-500">End of demo lifetime — after VP approval the vehicle leaves the operational fleet and goes to Accounts for sale.</p>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div><label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Final odometer *</label>
            <input data-testid="handover-odo-input" type="number" value={odo} onChange={(e) => setOdo(e.target.value)} className={inputCls} /></div>
          <div><label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Fuel level</label>
            <select data-testid="handover-fuel-select" value={fuel} onChange={(e) => setFuel(e.target.value)} className={inputCls}>
              {["E", "1/4", "1/2", "3/4", "F"].map((x) => <option key={x} value={x}>{x}</option>)}
            </select></div>
        </div>
        <div className="mt-2"><PhotoInput label="final odometer photo" value={odoPhoto} onChange={setOdoPhoto} testId="handover-odo-photo-btn" /></div>
        <textarea data-testid="handover-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Condition notes (scratches, dents, tyres…)" rows={2} className={inputCls} />
        <div className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Condition photos (min 4)</div>
        <div className="mt-1 grid grid-cols-2 gap-2">
          {slots.map(([k, l]) => <PhotoInput key={k} label={l} value={photos[k]} onChange={(p) => setPhotos({ ...photos, [k]: p })} testId={`handover-photo-${k}`} />)}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button onClick={onClose} className="rounded-xl border border-slate-200 py-3 text-sm font-bold text-slate-500">Cancel</button>
          <button data-testid="handover-submit-btn" onClick={submit} className="rounded-xl bg-[#0E2240] py-3 text-sm font-bold text-white">Raise Handover</button>
        </div>
      </div>
    </div>
  );
}

export default function VehicleDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [v, setV] = useState(null);
  const [handover, setHandover] = useState(false);
  const load = () => api.get(`/vehicles/${id}`).then((r) => setV(r.data)).catch(() => {});
  const [sched, setSched] = useState(null);
  const [range, setRange] = useState(7);
  useEffect(() => { api.get(`/vehicles/${id}/schedule`, { params: { days: range } }).then((r) => setSched(r.data)).catch(() => {}); }, [id, range]);
  useEffect(() => { load(); }, [id]);
  if (!v) return <div className="p-8 text-slate-400">Loading…</div>;
  const canBlock = ["sales", "sm", "tdc", "admin"].includes(user.role) && v.status === "available";
  const canHandover = ["sm", "admin"].includes(user.role) && ["available", "grounded"].includes(v.status);

  return (
    <div className="px-4 py-5 lg:px-8 max-w-3xl">
      <button data-testid="back-btn" onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-slate-500"><ArrowLeft size={15} /> Back</button>
      <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-[#0E2240]">{v.reg_no}</h1>
            <p className="text-sm text-slate-500">{v.model} {v.variant} · {v.fuel_type} · {v.color}</p>
          </div>
          <StatusChip status={v.status} />
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          {[["Odometer", `${(v.current_odometer || 0).toLocaleString("en-IN")} km`],
            ["Insurance", v.insurance_expiry], ["PUC", v.puc_expiry],
            ["Service due", `${v.service_due_date} / ${(v.service_due_km || 0).toLocaleString("en-IN")} km`],
            ["Inducted", v.induction_date || "—"], ["VIN", v.vin || "—"],
            ...(["aho", "azh", "vp", "md", "admin"].includes(user.role) ? [["Purchase value", v.purchase_value ? `₹${v.purchase_value.toLocaleString("en-IN")}` : "—"]] : [])].map(([l, val]) => (
            <div key={l} className="rounded-lg bg-slate-50 p-2.5">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">{l}</div>
              <div className="font-semibold text-[#0E2240] text-xs mt-0.5">{val}</div>
            </div>
          ))}
        </div>
        {sched && (
          <div data-testid="vehicle-schedule" className="mt-4 rounded-xl border border-slate-200 p-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-[#0E2240]">Schedule</h3>
              <div className="flex gap-1">
                {[[1, "Today"], [7, "Week"]].map(([n, l]) => <button key={n} data-testid={`schedule-range-${n}`} onClick={() => setRange(n)} className={`rounded-full px-3 py-1 text-[11px] font-bold ${range === n ? "bg-[#0E2240] text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>)}
              </div>
            </div>
            {sched.active_trips.map((t) => (
              <div key={t.id} className="mt-2 rounded-lg bg-indigo-50 px-3 py-2 text-xs text-indigo-800"><b>Out now</b> · {t.kec_name} → {t.customer_name} · since {d(t.gate_out_ts || t.started_ts)}</div>
            ))}
            {sched.bookings.length === 0 && sched.active_trips.length === 0 && <div className="mt-2 text-xs text-slate-400">No bookings in this range — free to promise a slot.</div>}
            {Object.entries(sched.bookings.reduce((acc, b) => { const k = new Date(b.slot_start).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", timeZone: "Asia/Kolkata" }); (acc[k] = acc[k] || []).push(b); return acc; }, {})).map(([day, items]) => (
              <div key={day} className="mt-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{day}</div>
                {items.map((b) => (
                  <div key={b.id} data-testid={`schedule-item-${b.id}`} className={`mt-1 flex items-center justify-between rounded-lg px-3 py-2 text-xs ${b.status === "blocked" ? "bg-amber-50 text-amber-800" : b.status === "slot_missed" ? "bg-red-50 text-red-700" : "bg-sky-50 text-sky-800"}`}>
                    <span><b>{new Date(b.slot_start).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" })}–{b.slot_end ? new Date(b.slot_end).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }) : ""}</b> · {b.type === "connected" ? `Connected (${(b.customers || []).length})` : b.type === "home" ? "Home TD" : b.type === "utility" ? "Utility" : "Showroom"} — {b.kec_name}</span>
                    <StatusChip status={b.status} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
        {v.odometer_corrections?.length > 0 && (
          <div data-testid="odo-corrections-history" className="mt-3 rounded-lg border border-slate-200 p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Odometer corrections</div>
            {v.odometer_corrections.map((c) => (
              <div key={c.id} className="mt-1.5 text-[11px] text-slate-600">
                Corrected from <b>{c.from.toLocaleString("en-IN")}</b> to <b>{c.to.toLocaleString("en-IN")} km</b> by {c.by_name}: “{c.reason}” <span className="text-slate-400">· {d(c.ts)}</span>
              </div>
            ))}
          </div>
        )}
        {v.active_block && (
          <div className="mt-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
            Blocked by <b>{v.active_block.kec_name}</b> for {v.active_block.customer_name} (ENQ {v.active_block.enquiry_id}) until {d(v.active_block.block_until)}
          </div>
        )}
        {canBlock && (
          <button data-testid="block-this-car-btn" onClick={() => nav(`/block/${v.id}`)}
            className="mt-4 w-full rounded-xl bg-[#BB162B] py-3 text-sm font-bold text-white active:scale-[0.98] transition-transform duration-150">Block this car</button>
        )}
        {canHandover && (
          <button data-testid="handover-btn" onClick={() => setHandover(true)}
            className="mt-2 w-full rounded-xl border border-slate-300 py-3 text-sm font-bold text-[#0E2240]">Hand over to Accounts (end of life)</button>
        )}
      </div>
      {handover && <HandoverModal v={v} onClose={() => setHandover(false)} onDone={load} />}

      {[["Trip History", v.trips, (t) => `${t.kec_name} · ${t.customer_name} · ${t.type} · ${t.km_driven ?? "—"} km`, (t) => d(t.created_at), (t) => t.status],
        ["Workshop History", v.workshop_history, (w) => `RO ${w.ro_number} · ${w.reason}${w.cost_amount ? ` · ₹${w.cost_amount.toLocaleString("en-IN")}` : ""}`, (w) => d(w.handover_dt), (w) => w.status],
        ["Fuel Logs", v.fuel_logs, (f) => `${f.litres} L · ₹${f.amount.toLocaleString("en-IN")} · ${f.odometer} km`, (f) => f.date, () => null],
        ["Damage Reports", v.damage_reports, (x) => `${x.severity} · ${x.description}`, (x) => d(x.created_at), (x) => x.status],
      ].map(([title, list, main, sub, chip]) => (
        <div key={title} className="mt-5">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{title}</h2>
          <div className="mt-2 space-y-2">
            {(list || []).length === 0 && <div className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">No records</div>}
            {(list || []).map((item) => (
              <div key={item.id} className="rounded-xl border border-slate-200 bg-white p-3 text-sm flex items-center justify-between gap-2">
                <div>
                  <div className="font-medium text-[#0E2240] text-[13px]">{main(item)}</div>
                  <div className="text-[11px] text-slate-400">{sub(item)}</div>
                </div>
                {chip(item) && <StatusChip status={chip(item)} />}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
